import { Form, Formik, FormikProps } from "formik";
// @material-ui/icons
import Typography from "@material-ui/core/Typography";
import { useMutation } from "@apollo/client";
// core components
import GridContainer from "@components/Grid/GridContainer.js";
import GridItem from "@components/Grid/GridItem.js";
import React, { useMemo, useState } from "react";
import { DISABLED, ENABLED, FREE, PAYG } from "../constants";
import { codeGenerator } from "../utils";
import { InitializeCodeGenInput } from "apps/things/app/types/thingsGlobalTypes";
import DaysGenInput from "./DaysGenInput";
import DeviceDetailsPreview from "./DeviceDetailsPreview";
import CodeGeneratorPreview from "./CodeGeneratorPreview";
import { CodeGeneratorContext } from "../context/CodeGeneratorProvider";
import {
  ButtonGroup,
  FormControl,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@material-ui/core";
import Button from "@components/CustomButtons/Button";
import { useAuth } from "admin/auth/context/AuthProvider";
import ActionModal from "@components/Modal/ActionModal";
import useNotifier from "hooks/useNotifier";
import useIncreaseFreeCodeCount from "../hooks/useIncreaseFreeCodeCount";
import useIncreaseResetCodeCount from "../hooks/useIncreaseResetCodeCount";
import OVSForm from "@components/Form/OVSForm";
import GoBackHeader from "@components/GoBackHeader/GoBackHeader";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import { makeStyles } from "@material-ui/core/styles";

import {
  Box,
  IconButton,
  InputAdornment,
  SelectChangeEvent,
} from "@mui/material";
import { HelpOutline } from "@material-ui/icons";
import { generateOpenToken } from "../mutations";
import CustomInput from "@components/CustomInput/CustomInput";
import CustomSelect from "@components/CustomSelect/CustomSelect";

const TOKEN_TYPE_MAPPING = {
  ADD_TIME: "ADD_TIME",
  SET_TIME: "SET_TIME",
  COUNTER_SYNC: "COUNTER_SYNC",
  DISABLE_PAYG: "DISABLE_PAYG",
};

const viewPortWidth = window.innerWidth;
const thresholdWidth = 600;

const useStyles = makeStyles({
  container: {
    marginBottom: 30,
    marginTop: 10,
  },
  table: {
    minWidth: viewPortWidth <= thresholdWidth ? 280 : 650,
  },
  td: {
    fontSize: viewPortWidth <= thresholdWidth ? "10px" : "16px",
    padding: viewPortWidth <= thresholdWidth ? "16px 5px" : "16px",
  },
  th: {
    width: viewPortWidth <= thresholdWidth ? 100 : 180,
    fontWeight: "bold",
    fontSize: viewPortWidth <= thresholdWidth ? "10px" : "16px",
    padding: viewPortWidth <= thresholdWidth ? "16px 5px" : "16px",
  },
  btn: { padding: 0, marginLeft: 15 },
  thWithButton: { paddingTop: 7, paddingBottom: 7 },
});

const CodeGeneratorForm: React.FC<{
  formBag: FormikProps<InitializeCodeGenInput>;
  loading: boolean;
  actionButtonName?: string;
  handleResetCode: () => void;
  handleFreeCode: () => void;
  handleDaysCode: (days: number) => void;
  oemItemID?: string;
  itemId?: string;
  handleCreateCodeEvent: (payload: any) => void;
  handleCreateOrUpdateAvatar: (
    remDays: string,
    payGoState: string,
    deviceState: string,
    decHashTop: string,
    daysPassed: string
  ) => void;
}> = (props) => {
  const {
    formBag,
    loading,
    handleCreateOrUpdateAvatar,
    handleDaysCode,
    handleFreeCode: handleCreateFreeCode,
    handleResetCode: handleCreateResetCode,
  } = props;

  const {
    setRemDays,
    setDecHashTop,
    itemID,
    item,
    oemItemID,
    daysRemaining,
    daysPassed,
    setDaysRemaining,
    setDaysPassed,
    setHashIndex,
    codeDays,
    hashTop,
    hashRoot,
    hashIndex,
    setOtpHex,
    otpCount,
    maxHashJump,
    setCodeDays,
    setCodeAccepted,
    otpHex,
    otpDec,
    otpDecEntry,
    setOtpDecEntry,
    hashChainLength,
    codeAccepted,
    payGoState,
    setPayGoState,
    deviceState,
    setDeviceState,
    // remDays,
  } = React.useContext(CodeGeneratorContext);
  //console.log("item", item?.itemBatch?.code_gen_type);

  const [generatedToken, setGeneratedToken] = useState<{
    token: string;
  } | null>(null);
  const [generateToken, { loading: tokenLoading, error }] = useMutation(
    generateOpenToken,
    {
      onCompleted: (data) => {
        if (data?.generateOpenToken?.token) {
          setGeneratedToken(data.generateOpenToken as { token: string });
        }
      },
    }
  );

  // Handle token type selection
  const handleTokenTypeChange = (
    event: SelectChangeEvent<keyof typeof TOKEN_TYPE_MAPPING | "">
  ) => {
    setTokenType(event.target.value as keyof typeof TOKEN_TYPE_MAPPING | "");
  };

  // Handle token value input
  const handleTokenValueChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    // Only allow numeric input
    const value = event.target.value.replace(/[^0-9]/g, "");
    setTokenValue(value);
  };

  // Generate token on button click
  const handleGenerateToken = () => {
    if (!tokenType || !tokenValue) {
      // You might want to add validation feedback here
      return;
    }

    generateToken({
      variables: {
        itemId: itemID,
        token_type: tokenType ? TOKEN_TYPE_MAPPING[tokenType] : undefined,
        token_value: parseInt(tokenValue, 10),
      },
    });
  };

  const [errors, setErrors] = useState("");
  const [codeGenErrors, setCodeGenErrors] = useState("");
  const { isDistributor, disableFreeCode } = useAuth();
  console.log("isDistributor", isDistributor);
  const notify = useNotifier();
  const [confirmDayCode, setConfirmDayCode] = useState(false);
  const [confirmOpenToken, setConfirmOpenToken] = useState(false);
  const [confirmResetCode, setConfirmResetCode] = useState(false);
  const [confirmFreeCode, setConfirmFreeCode] = useState(false);
  const [increaseResetCodeModalOpen, setIncreaseResetCodeModalOpen] =
    useState(false);
  const [increaseFreeCodeModalOpen, setIncreaseFreeCodeModalOpen] =
    useState(false);
  const [freeCodeCount, setFreeCodeCount] = useState(0);
  const [resetCodeCount, setResetCodeCount] = useState(0);
  const [tokenType, setTokenType] = useState<
    keyof typeof TOKEN_TYPE_MAPPING | ""
  >("");
  const [tokenValue, setTokenValue] = useState("");

  const { mutation: increaseFreeCode, mutationOpts: increaseFreeCodeOpts } =
    useIncreaseFreeCodeCount({
      onComplete: async () => {},
    });
  const { mutation: increaseResetCode, mutationOpts: increaseResetCodeOpts } =
    useIncreaseResetCodeCount({
      onComplete: async () => {},
    });

  const handleIncreaseResetCode = async () => {
    if (!resetCodeCount) {
      return notify({ text: "Please add reset code count.", status: "error" });
    }

    await increaseResetCode({
      variables: {
        itemId: formBag.values.itemId,
        resetCodeCount: resetCodeCount,
      },
    });

    setIncreaseResetCodeModalOpen(false);
  };

  const handleIncreaseFreeCode = async () => {
    if (!freeCodeCount) {
      return notify({ text: "Please add free code count.", status: "error" });
    }

    await increaseFreeCode({
      variables: {
        itemId: formBag.values.itemId,
        freeCodeCount: freeCodeCount,
      },
    });
    setIncreaseFreeCodeModalOpen(false);
  };

  const freeFormFieldsData = useMemo(
    () => [
      {
        name: "",
        fields: [
          {
            md: 12,
            type: "number",
            name: "freeCodeCount",
            label: "Free Code Count",
            onChange: (e: any) => {
              setFreeCodeCount(parseInt(e.target.value));
            },
          },
        ],
      },
    ], // eslint-disable-next-line
    [freeCodeCount]
  );
  const formFieldsData = useMemo(
    () => [
      {
        name: "",
        fields: [
          {
            md: 12,
            type: "number",
            name: "resetCodeCount",
            label: "Reset Code Count",
            onChange: (e: any) => {
              setResetCodeCount(parseInt(e.target.value));
            },
          },
        ],
      },
    ], // eslint-disable-next-line
    [resetCodeCount]
  );

  const handleDaysPassedPlusOne = () => {
    const days = daysRemaining - 1;
    if (days > -1) {
      let total_days = daysRemaining - 1;
      let days_passed = daysPassed + 1;
      let device_state = deviceState;
      if (total_days > -1) {
        device_state = DISABLED;
      }
      setDaysPassed(daysPassed + 1);
      handleCreateOrUpdateAvatar(
        total_days.toString(),
        payGoState,
        device_state,
        otpDecEntry,
        days_passed.toString()
      );
    } else {
      setDeviceState("DISABLED");
    }
  };

  const handleDaysChange = () => {
    if (codeGenErrors) return;
    handleDaysCode(codeDays);
    setHashIndex(hashIndex - codeDays);
    setCodeDays(0);
  };

  const handleFreeCode = () => {
    const code_days = codeGenerator.max_HCJ;
    handleCreateFreeCode();
    setCodeDays(code_days);
    // const hash_top = codeGenerator.OTP_hex(hashRoot, hashIndex, code_days);
    // generateCodeGenInformation(hash_top, code_days, CodeTypes.FREECODE);
  };

  const handleResetCode = () => {
    handleCreateResetCode();
  };

  const handleSetDayCode = (e: number) => {
    if (e > codeGenerator.codeLimit || e === 0) {
      setCodeGenErrors(`Maximum day code is ${codeGenerator.codeLimit}.`);
      return;
    }
    setCodeGenErrors("");
    setCodeDays(e);
  };

  const generateDeviceInformation = async () => {
    // validate otp dec entry
    if (otpDecEntry.trim().length !== 29) {
      setErrors("Please enter a valid OTP code");
      return;
    }

    if (!otpDecEntry.includes("*") || !otpDecEntry.includes("#")) {
      setErrors("Please enter a valid OTP code");
      return;
    }

    setErrors("");

    const otp_hex = codeGenerator.padded_decimal_to_hex(otpDecEntry);
    setDecHashTop(otpDecEntry);
    const current_hash_index = codeGenerator.get_hash_index(hashRoot, otp_hex);
    let accepted = true;
    let pay_go_state = PAYG;
    let device_state = ENABLED;

    // Error conditions
    if (current_hash_index < codeGenerator.freeCodeJump) {
      console.warn(
        "// condition 1: a hit of daysCode in the range [1, $freeCodeJump-1]"
      );
      setPayGoState(PAYG);
      setDeviceState(ENABLED);
    } else if (
      current_hash_index >= codeGenerator.freeCodeJump &&
      current_hash_index < codeGenerator.resetCodeJump
    ) {
      console.warn(
        "// condition 2: a hit of FREECODE in the range [$freeCodeJump, $resetCodeJump-1]"
      );
      setPayGoState(FREE);
      pay_go_state = FREE;
      setDeviceState(ENABLED);
    } else if (
      current_hash_index >= codeGenerator.resetCodeJump &&
      current_hash_index < codeGenerator.codeLimit
    ) {
      console.warn(
        "// condition 3: a hit of RESETCODE in the range [$resetCodeJump, $codeLimit -1]"
      );
      setPayGoState(PAYG);
      setDeviceState(DISABLED);
      device_state = DISABLED;
    } else {
      console.warn("code rejected", current_hash_index);
      setCodeAccepted(false);
      accepted = false;
      setErrors("Code rejected.");
    }

    //set state
    if (accepted) {
      const total_days = current_hash_index - daysPassed + "d";
      setCodeAccepted(true);
      setDaysRemaining(current_hash_index - daysPassed);
      setRemDays(current_hash_index + "d");
      setOtpHex(otp_hex);

      // update avatar
      handleCreateOrUpdateAvatar(
        total_days,
        pay_go_state,
        device_state,
        otpDecEntry,
        daysPassed.toString()
      );
    }
  };

  const handleSetDecEntry = (e: string) => {
    setErrors("");
    setOtpDecEntry(e.trim());
  };
  const thresholdWidth = 600;

  const formStyleMobile = { paddingTop: 10, marginBottom: 10 };
  const formStyleDesktop = {
    paddingTop: 10,
    marginBottom: 10,
    marginRight: 10,
  };

  const classes = useStyles();

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <Form>
          <GridContainer>
            {item?.itemBatch?.code_gen_type === "OPENTOKEN" && (
              <GridContainer>
                <GridItem xs={12}>
                  <Box style={{ marginLeft: 15 }}>
                    <GoBackHeader />
                  </Box>

                  <Typography
                    variant="h5"
                    gutterBottom
                    style={{ marginLeft: 15 }}
                  >
                    Code Generator
                  </Typography>
                </GridItem>
                <GridItem xs={12}>
                  <GridContainer>
                    <GridItem md={6} xs={12}>
                      <Box style={{ marginLeft: 20 }}>
                        <CustomSelect
                          value={tokenType}
                          required
                          onChange={handleTokenTypeChange}
                          label="Token Type"
                          selectTitle="Select token type"
                          name="tokenType"
                          selectOptions={[
                            { _id: "ADD_TIME", name: "Add time" },
                            { _id: "SET_TIME", name: "Set Time" },
                            { _id: "COUNTER_SYNC", name: "Counter Sync" },
                            { _id: "DISABLE_PAYG", name: "Disable PayG" },
                          ]}
                        />
                      </Box>
                    </GridItem>
                    <GridItem md={6} xs={12}>
                      <CustomInput
                        id="token-input"
                        name="token"
                        value={tokenValue}
                        onChange={handleTokenValueChange}
                        labelText="Enter token value"
                        formControlProps={{
                          fullWidth: true,
                        }}
                        inputProps={{
                          placeholder: "Enter token value",
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton edge="end">
                                <HelpOutline fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        success={false}
                        error={false}
                      />
                    </GridItem>

                    <GridItem xs={12}>
                      <Box
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "start",
                          gap: 2,
                          marginTop: 1,
                          padding: 2,
                          borderRadius: 1,
                          marginLeft: 15,
                        }}
                      >
                        <Button
                          color="primary"
                          variant="contained"
                          sx={{
                            borderRadius: 2,
                            px: 4,
                            textTransform: "none",
                            boxShadow: 0,
                            "&:hover": { boxShadow: 2 },
                          }}
                          onClick={() => setConfirmOpenToken(true)}
                        >
                          {tokenLoading ? <>Loading</> : <>Generate Token</>}
                        </Button>

                        <>
                          <TableContainer
                            component={Paper}
                            className={classes.container}
                          >
                            <Table
                              className={classes.table}
                              aria-label="simple table"
                            >
                              <TableBody>
                                <TableRow>
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className={classes.th}
                                  >
                                    OTP
                                  </TableCell>
                                  <TableCell
                                    className={classes.td}
                                    component="td"
                                  >
                                    {generatedToken?.token}
                                    {generatedToken && (
                                      <span>
                                        <IconButton
                                          onClick={() => {
                                            navigator.clipboard.writeText(
                                              generatedToken.token
                                            );
                                            notify({
                                              text: "Otp Copied Successfully",
                                              status: "success",
                                            });
                                          }}
                                          className={classes.btn}
                                        >
                                          <FileCopyOutlinedIcon
                                            style={{ fontSize: 16 }}
                                            fontSize="inherit"
                                          />
                                        </IconButton>
                                      </span>
                                    )}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </>

                        {error && (
                          <Typography color="error" variant="body2">
                            Error generating token. Please try again.
                          </Typography>
                        )}
                      </Box>
                    </GridItem>
                  </GridContainer>
                </GridItem>
              </GridContainer>
            )}
            {item?.itemBatch?.code_gen_type !== "OPENTOKEN" && (
              <GridItem md={isDistributor ? 12 : 6} xs={12}>
                <GridContainer>
                  <GridItem xs={12}>
                    <GoBackHeader />
                    <Typography variant="h5" gutterBottom>
                      Code Generator
                    </Typography>
                  </GridItem>
                  <GridItem md={6} xs={12}>
                    <DaysGenInput
                      onChange={handleSetDayCode}
                      onBtnClick={() => {
                        if (!codeDays) {
                          notify({
                            status: "error",
                            text: "OTP code days should be more than 0",
                          });
                          return;
                        }
                        setConfirmDayCode(true);
                      }}
                      loading={loading}
                      errors={codeGenErrors}
                    />
                  </GridItem>
                  <GridItem md={6} xs={12}>
                    <FormControl style={{ paddingTop: 21 }}>
                      <ButtonGroup
                        disableElevation
                        variant="contained"
                        color="primary"
                        orientation={
                          window.innerWidth <= thresholdWidth
                            ? "vertical"
                            : "horizontal"
                        }
                        style={{ width: "200px" }}
                      >
                        <Button
                          size="sm"
                          onClick={() => setConfirmResetCode(true)}
                        >
                          RESET CODE
                        </Button>
                        {!disableFreeCode ? (
                          <Button // TODO disable free code for Buvilian client>
                            size="sm"
                            onClick={() => setConfirmFreeCode(true)}
                          >
                            FREE CODE
                          </Button>
                        ) : null}
                      </ButtonGroup>
                    </FormControl>
                  </GridItem>

                  <GridItem md={8} xs={12}>
                    <FormControl
                      style={
                        window.innerWidth <= thresholdWidth
                          ? formStyleMobile
                          : formStyleDesktop
                      }
                    >
                      <ButtonGroup
                        disableElevation
                        variant="contained"
                        color="primary"
                        orientation="vertical"
                        style={{ width: "200px" }}
                      >
                        <Button
                          title="Increase Reset Code"
                          size="sm"
                          onClick={() => setIncreaseResetCodeModalOpen(true)}
                        >
                          INCREASE RESET CODE
                        </Button>
                        <Button // TODO disable free code for Buvilian client>
                          size="sm"
                          onClick={() => setIncreaseFreeCodeModalOpen(true)}
                        >
                          INCREASE FREE CODE
                        </Button>
                      </ButtonGroup>
                    </FormControl>
                  </GridItem>

                  <GridItem xs={12}></GridItem>
                  <GridItem xs={12}>
                    <CodeGeneratorPreview
                      otpHex={otpHex}
                      otpDec={otpDec}
                      hashIndex={hashIndex}
                      otpCount={otpCount}
                      hashTop={hashTop}
                      hashRoot={hashRoot}
                      hashChainLength={hashChainLength}
                      maxHashJump={maxHashJump}
                      oemItemID={oemItemID}
                      itemID={itemID}
                      customerName={
                        item?.assetAccount?.credit?.owner?.name || ""
                      }
                      paymentHistory={[]}
                    />
                  </GridItem>
                </GridContainer>
              </GridItem>
            )}
            {!isDistributor &&
              item?.itemBatch?.code_gen_type !== "OPENTOKEN" && (
                <GridItem xs={12} md={6}>
                  <GridItem xs={12}>
                    <div
                      style={{ visibility: "hidden", marginBottom: "0.6rem" }}
                    >
                      Position
                    </div>
                    <Typography variant="h5" gutterBottom>
                      Device
                    </Typography>
                  </GridItem>
                  <GridItem xs={12}>
                    <DaysGenInput
                      type="text"
                      placeholder="Enter an OTP string here..."
                      btnText="Parse"
                      onChange={handleSetDecEntry}
                      onBtnClick={generateDeviceInformation}
                      success={codeAccepted}
                      errors={errors}
                    />
                  </GridItem>
                  <GridItem xs={12}>
                    <DeviceDetailsPreview
                      daysPassed={daysPassed}
                      daysRemaining={daysRemaining}
                      payGoState={payGoState}
                      deviceState={deviceState}
                      hashTop={hashTop}
                      hashRoot={hashRoot}
                      hashChainLength={hashChainLength}
                      maxHashJump={maxHashJump}
                      oemItemID={oemItemID}
                      itemID={itemID}
                      daysPassedPlusOne={handleDaysPassedPlusOne}
                      loading={loading}
                    />
                  </GridItem>
                </GridItem>
              )}
            : {null}
          </GridContainer>
        </Form>
        <ActionModal
          isModalOpen={confirmOpenToken}
          toggleModal={setConfirmOpenToken}
          handleOnClickOkey={() => {
            handleGenerateToken();
            setConfirmOpenToken(false);
          }}
          handleOnClickCancel={() => setConfirmOpenToken(false)}
          okText={"Confirm"}
        >
          <div>
            Are you sure you want to generate this token? This action is not
            reversible.
          </div>
        </ActionModal>

        <ActionModal
          isModalOpen={confirmDayCode}
          toggleModal={setConfirmDayCode}
          handleOnClickOkey={() => {
            handleDaysChange();
            setConfirmDayCode(false);
          }}
          handleOnClickCancel={() => setConfirmDayCode(false)}
          okText={"Confirm"}
        >
          <div>
            Are you sure you want to generate day code? This action is not
            reversible.
          </div>
        </ActionModal>

        <ActionModal
          isModalOpen={confirmFreeCode}
          toggleModal={setConfirmFreeCode}
          handleOnClickOkey={() => {
            handleFreeCode();
            setConfirmFreeCode(false);
          }}
          handleOnClickCancel={() => setConfirmFreeCode(false)}
          okText={"Confirm"}
        >
          <div>
            Are you sure you want to generate Free code? This action is not
            reversible.
          </div>
        </ActionModal>

        <ActionModal
          isModalOpen={confirmResetCode}
          toggleModal={setConfirmResetCode}
          handleOnClickOkey={() => {
            handleResetCode();
            setConfirmResetCode(false);
          }}
          handleOnClickCancel={() => setConfirmResetCode(false)}
          okText={"Confirm"}
        >
          <div>
            Are you sure you want to generate Reset code? This action is not
            reversible.
          </div>
        </ActionModal>
        {increaseResetCodeModalOpen ? (
          <ActionModal
            isModalOpen={increaseResetCodeModalOpen}
            toggleModal={setIncreaseResetCodeModalOpen}
            handleOnClickOkey={() => {
              handleIncreaseResetCode();
            }}
            handleOnClickCancel={() => setIncreaseResetCodeModalOpen(false)}
            okText={"Save"}
            loading={increaseResetCodeOpts.loading}
          >
            <GridContainer>
              <Formik onSubmit={(e) => {}} initialValues={{}}>
                {(formBag) => (
                  <>
                    <OVSForm formFieldsData={formFieldsData} />
                  </>
                )}
              </Formik>
            </GridContainer>
          </ActionModal>
        ) : null}

        {increaseFreeCodeModalOpen ? (
          <ActionModal
            isModalOpen={increaseFreeCodeModalOpen}
            toggleModal={setIncreaseFreeCodeModalOpen}
            handleOnClickOkey={() => {
              handleIncreaseFreeCode();
            }}
            handleOnClickCancel={() => setIncreaseFreeCodeModalOpen(false)}
            okText={"Save"}
            loading={increaseFreeCodeOpts.loading}
          >
            <GridContainer>
              <Formik onSubmit={(e) => {}} initialValues={{}}>
                {(formBag) => (
                  <>
                    <OVSForm formFieldsData={freeFormFieldsData} />
                  </>
                )}
              </Formik>
            </GridContainer>
          </ActionModal>
        ) : null}
      </GridItem>
    </GridContainer>
  );
};

export default CodeGeneratorForm;
